/**
 * CheckoutPage - Cart review, terms acceptance, and Stripe payment
 * Handles multi-class booking checkout with $100 deposit per class
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../context/AuthContext';
import { useCheckout, formatBookingDate, type CartItem } from '../../context/CheckoutContext';
import { getContacts, createContact, type Contact, type CreateContactData } from '../../api/studios';
import {
  createBooking,
  confirmBooking,
  getCurrentTerms,
  getStripePublishableKey,
  formatPrice,
  type TermsVersion,
  type CreateBookingResponse,
} from '../../api/booking';
import { getPublicSystemSettings, type PublicSystemSettings } from '../../api/events';
import { getFriendlyErrorMessage } from '../../api/client-error';
import { resolveSessionTimes, formatTimeDisplay, addMinutes, type SessionMode } from '@sda/shared';
import { CLASS_TIME_SLOTS } from '../../api/events';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Convert internal student level value to user-friendly label
 */
function formatStudentLevel(level: string | null | undefined): string {
  if (!level) return 'All levels';

  const levelLabels: Record<string, string> = {
    'type_a': 'Beginner',
    'type_b': 'Intermediate (Ages 9-12)',
    'type_c': 'Intermediate/Advanced (Ages 12+)',
    'level_1': 'Level 1: Beginner',
    'level_2': 'Level 2: Intermediate',
    'level_3': 'Level 3: Intermediate/Advanced',
    'beginner': 'Beginner',
    'intermediate': 'Intermediate',
    'advanced': 'Advanced',
    'mixed': 'Mixed Levels',
  };

  return levelLabels[level] || level;
}

// =============================================================================
// Types
// =============================================================================

interface CheckoutState {
  step: 'review' | 'terms' | 'payment' | 'processing';
  termsVersion: TermsVersion | null;
  contacts: Contact[];
  bookingResponse: CreateBookingResponse | null;
  stripePromise: Promise<Stripe | null> | null;
}

// =============================================================================
// Main Checkout Page
// =============================================================================

export default function CheckoutPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    items,
    isLoading: cartLoading,
    removeItem,
    updateItem,
    getCartSummary,
    appliedCoupon,
    acceptTerms,
    selectedContactId,
    setSelectedContactId,
    clearCart,
  } = useCheckout();

  const [state, setState] = useState<CheckoutState>({
    step: 'review',
    termsVersion: null,
    contacts: [],
    bookingResponse: null,
    stripePromise: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemSettings, setSystemSettings] = useState<PublicSystemSettings | null>(null);
  const [contactsLoaded, setContactsLoaded] = useState(false);

  // Keep a ref to selectedContactId so the loadData effect can read the current
  // value without adding it to the dependency array (which would cause a re-run
  // loop every time the user picks a contact).
  const selectedContactIdRef = useRef(selectedContactId);
  useEffect(() => {
    selectedContactIdRef.current = selectedContactId;
  }, [selectedContactId]);

  const summary = getCartSummary();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent('/checkout')}`);
    }
  }, [user, navigate]);

  // Load contacts and terms on mount
  useEffect(() => {
    if (!user) return; // Don't load data if not authenticated

    const loadData = async () => {
      try {
        const [contactsData, termsData, stripeKey, settingsData] = await Promise.all([
          getContacts(),
          getCurrentTerms(),
          getStripePublishableKey(),
          getPublicSystemSettings().catch(() => null),
        ]);
        if (settingsData) setSystemSettings(settingsData);

        // Ensure contacts is always an array (defensive check)
        const contactsArray = Array.isArray(contactsData) ? contactsData : [];

        setState((prev) => ({
          ...prev,
          contacts: contactsArray,
          termsVersion: termsData,
          stripePromise: loadStripe(stripeKey, { locale: 'en' }),
        }));
        setContactsLoaded(true);

        // Auto-select: prefer the primary contact; fall back to the first contact in the list.
        // Read via ref so we don't add selectedContactId to the dep array (which would
        // cause this effect to re-run every time the user picks a contact).
        //
        // Validate the persisted selectedContactId against the loaded list — localStorage
        // can carry a stale ID from a prior session (a contact that was edited/deleted, or
        // belongs to a studio the user no longer has access to). If the stored ID doesn't
        // map to any loaded contact, treat it as unset and run the auto-select.
        const storedId = selectedContactIdRef.current;
        const storedIdIsValid = storedId !== null && contactsArray.some((c) => c.id === storedId);
        const primaryContact = contactsArray.find((c) => c.is_primary);
        const defaultContact = primaryContact ?? contactsArray[0];
        if (defaultContact && !storedIdIsValid) {
          setSelectedContactId(defaultContact.id);
        }
      } catch (err) {
        setContactsLoaded(true);
        setError(getFriendlyErrorMessage(err, 'Failed to load checkout data'));
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if cart is empty (only matters during review step)
  const isCartEmpty = !cartLoading && items.length === 0 && state.step === 'review';

  // Handle proceed to terms
  const handleProceedToTerms = () => {
    if (!selectedContactId) {
      setError('Please select a contact for this booking');
      return;
    }
    setState((prev) => ({ ...prev, step: 'terms' }));
  };

  // Handle accept terms and proceed to payment
  const handleAcceptTerms = async () => {
    if (!state.termsVersion || !selectedContactId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Accept terms in context
      acceptTerms(state.termsVersion.version);

      // Create booking with payment intent
      const bookingInput = {
        classes: items.map((item) => {
          // Monday items (slotStartTime present) or stale custom-slot items bypass sessionIdentifier
          const isCustomItem = !!item.slotStartTime || !CLASS_TIME_SLOTS.includes(item.timeSlot as '9:00 AM' | '11:00 AM');
          return {
            classOptionId: item.classOptionId,
            tourStopId: item.tourStopId,
            scheduledDate: item.scheduledDate,
            // Monday/custom items: omit sessionIdentifier, send startTime instead
            sessionIdentifier: isCustomItem
              ? undefined
              : (item.sessionIdentifier || (item.timeSlot === '9:00 AM' ? 'session_1' as const : 'session_2' as const)),
            startTime: item.slotStartTime,
            // Always send timeSlot — backend uses it as fallback for stale carts with no startTime
            timeSlot: item.timeSlot,
            studentCount: item.studentCount,
            studentLevel: item.studentLevel,
            specialRequests: item.specialRequests,
            basePriceCents: item.basePriceCents,
            travelSurchargeCents: item.travelSurchargeCents,
          };
        }),
        contactId: selectedContactId,
        includeMarketingMaterials: items.some((item) => item.includeMarketingMaterials),
        groupTicketInquiry: items.some((item) => item.groupTicketInquiry),
        termsVersion: state.termsVersion.version,
        termsAcceptedAt: new Date().toISOString(),
        couponCode: appliedCoupon?.code,
        startTimePreference: items[0]?.startTimePreference || 'default',
        simultaneousSessions: items.some((i) => i.simultaneousSessions),
        simultaneousTrigger: items.find((i) => i.simultaneousTrigger)?.simultaneousTrigger || null,
      };

      const response = await createBooking(bookingInput);

      setState((prev) => ({
        ...prev,
        step: 'payment',
        bookingResponse: response,
      }));
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Failed to create booking'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state (also while redirecting to login)
  if (cartLoading || !user) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  // Show empty cart state instead of redirecting
  if (isCartEmpty) {
    return (
      <div className="min-h-screen bg-warm-50">
        {/* Header */}
        <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="Stage Door Access" className="h-7" />
              </Link>

              <div className="flex items-center gap-4">
                <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
                <button
                  onClick={() => logout()}
                  className="text-warm-600 hover:text-warm-900 text-sm font-medium"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Empty Cart Content */}
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-warm-100 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-warm-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-display font-bold text-warm-900 mb-3">
              Your cart is empty
            </h1>
            <p className="text-warm-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added any masterclasses to your cart yet.
              Browse our upcoming classes and find the perfect one for your students!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/events"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                Browse Classes
              </Link>
              <Link
                to="/dashboard"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Go to Dashboard
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {['review', 'terms', 'payment'].map((stepName, index) => {
              const stepNumber = index + 1;
              const isActive = state.step === stepName;
              const isCompleted =
                (state.step === 'terms' && stepName === 'review') ||
                (state.step === 'payment' && stepName !== 'payment') ||
                (state.step === 'processing' && stepName !== 'payment');

              return (
                <div key={stepName} className="flex items-center">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${isCompleted ? 'bg-success-500 text-white' : ''}
                      ${isActive ? 'bg-primary-600 text-white' : ''}
                      ${!isActive && !isCompleted ? 'bg-warm-200 text-warm-500' : ''}
                    `}
                  >
                    {isCompleted ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      stepNumber
                    )}
                  </div>
                  <span
                    className={`
                      ml-2 text-sm font-medium hidden sm:block
                      ${isActive ? 'text-primary-600' : 'text-warm-500'}
                    `}
                  >
                    {stepName === 'review' && 'Review Cart'}
                    {stepName === 'terms' && 'Terms & Conditions'}
                    {stepName === 'payment' && 'Payment'}
                  </span>
                  {index < 2 && (
                    <div className="w-8 sm:w-16 h-0.5 bg-warm-200 ml-2 sm:ml-4" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {/* Step: Review Cart */}
        {state.step === 'review' && (
          <CartReviewStep
            items={items}
            summary={summary}
            contacts={state.contacts}
            contactsLoaded={contactsLoaded}
            selectedContactId={selectedContactId}
            onSelectContact={setSelectedContactId}
            onRemoveItem={removeItem}
            onUpdateItem={updateItem}
            onProceed={handleProceedToTerms}
            onContactCreated={(contact) => {
              setState((prev) => ({ ...prev, contacts: [...prev.contacts, contact] }));
              setSelectedContactId(contact.id);
            }}
            systemSettings={systemSettings}
          />
        )}

        {/* Step: Terms & Conditions */}
        {state.step === 'terms' && state.termsVersion && (
          <TermsStep
            terms={state.termsVersion}
            summary={summary}
            isLoading={isLoading}
            selectedContactId={selectedContactId}
            onAccept={handleAcceptTerms}
            onBack={() => setState((prev) => ({ ...prev, step: 'review' }))}
          />
        )}

        {/* Step: Payment */}
        {state.step === 'payment' && state.bookingResponse && state.stripePromise && (
          <Elements
            stripe={state.stripePromise}
            options={{
              clientSecret: state.bookingResponse.paymentIntentClientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#3B82F6',
                  borderRadius: '8px',
                },
              },
            }}
          >
            <PaymentStep
              bookingResponse={state.bookingResponse}
              onSuccess={(bookingId) => {
                clearCart();
                navigate(`/checkout/confirmation/${bookingId}`);
              }}
              onBack={() => setState((prev) => ({ ...prev, step: 'terms' }))}
            />
          </Elements>
        )}
      </main>
    </div>
  );
}

// =============================================================================
// Cart Review Step
// =============================================================================

interface CartReviewStepProps {
  items: ReturnType<typeof useCheckout>['items'];
  summary: ReturnType<typeof useCheckout>['getCartSummary'] extends () => infer R ? R : never;
  contacts: Contact[];
  contactsLoaded: boolean;
  selectedContactId: string | null;
  onSelectContact: (id: string | null) => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (itemId: string, updates: Partial<CartItem>) => void;
  onProceed: () => void;
  onContactCreated: (contact: Contact) => void;
  systemSettings: PublicSystemSettings | null;
}

function CartReviewStep({
  items,
  summary,
  contacts,
  contactsLoaded,
  selectedContactId,
  onSelectContact,
  onRemoveItem,
  onUpdateItem,
  onProceed,
  onContactCreated,
  systemSettings,
}: CartReviewStepProps) {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const { appliedCoupon, couponError, isValidatingCoupon, applyCoupon, removeCoupon } = useCheckout();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Cart Items */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-display font-bold text-warm-900">Review Your Cart</h2>
          <Link
            to="/events"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add More Classes
          </Link>
        </div>

        {items.map((item) => (
          <div key={item.id} className="card">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Show Logo */}
              <div className="w-16 h-16 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                {item.showLogoUrl ? (
                  <img
                    src={item.showLogoUrl}
                    alt={item.showName}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <span className="text-primary-500 text-xl font-display font-bold">
                    {item.showName.charAt(0)}
                  </span>
                )}
              </div>

              {/* Item Details */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-warm-900">{item.className}</h3>
                <p className="text-sm text-warm-600">{item.showName}</p>
                {/* Tour-stop context only (city the show plays in) — the class
                    itself happens at the buyer's own studio, so never present
                    the theater as the venue. */}
                {item.venueCity && (
                  <p className="text-sm text-warm-500">
                    Tour stop: {item.venueCity}{item.venueState ? `, ${item.venueState}` : ''}
                  </p>
                )}
                <p className="text-sm text-warm-600 mt-1">
                  {formatBookingDate(item.scheduledDate)} • {(() => {
                    // Monday item: slotStartTime is the exact server-provided time
                    if (item.slotStartTime) {
                      const endTime = item.slotEndTime
                        ?? (systemSettings ? addMinutes(item.slotStartTime, systemSettings.session_duration_minutes) : undefined);
                      return `${formatTimeDisplay(item.slotStartTime)}${endTime ? ` – ${formatTimeDisplay(endTime)}` : ''}`;
                    }
                    // Stale cart with a custom timeSlot (no slotStartTime) — show the key text
                    if (item.timeSlot && !CLASS_TIME_SLOTS.includes(item.timeSlot as '9:00 AM' | '11:00 AM')) {
                      return item.timeSlot;
                    }
                    // Standard Sat/Sun session — resolve via session times
                    if (!systemSettings) return 'Loading…';
                    if (!item.startTimePreference) return '—';
                    try {
                      // Derive mode from cart contents, mirroring bookings.ts:
                      // a '9:00 AM' item with a sibling '11:00 AM' item for the same
                      // tour stop + date is back_to_back (or simultaneous); without
                      // a sibling it is single.  A '11:00 AM' item with a sibling is
                      // back_to_back/simultaneous; without one it is second_slot.
                      const siblingSlot = item.timeSlot === '9:00 AM' ? '11:00 AM' : '9:00 AM';
                      const hasSibling = items.some(
                        (other) =>
                          other.id !== item.id &&
                          other.tourStopId === item.tourStopId &&
                          other.scheduledDate === item.scheduledDate &&
                          other.timeSlot === siblingSlot,
                      );
                      let checkoutMode: SessionMode;
                      if (item.timeSlot === '9:00 AM') {
                        checkoutMode = hasSibling
                          ? (item.simultaneousSessions ? 'simultaneous' : 'back_to_back')
                          : 'single';
                      } else {
                        // '11:00 AM'
                        checkoutMode = hasSibling
                          ? (item.simultaneousSessions ? 'simultaneous' : 'back_to_back')
                          : 'second_slot';
                      }
                      const resolved = resolveSessionTimes(
                        {
                          defaultSessionStartTime: systemSettings.default_session_start_time,
                          matineeTime: null,
                          sessionDurationMinutes: systemSettings.session_duration_minutes,
                          matineeThresholdTime: systemSettings.matinee_threshold_time,
                          defaultMatineeTime: systemSettings.default_matinee_time,
                        },
                        item.startTimePreference,
                        checkoutMode,
                      );
                      const session = item.timeSlot === '9:00 AM' ? resolved.session1 : resolved.session2;
                      const timeStr = `${formatTimeDisplay(session.start)} – ${formatTimeDisplay(session.end)}`;
                      if (item.startTimePreference !== 'default') {
                        return <>{timeStr} <span className="text-amber-600 text-xs">(preferred, pending confirmation)</span></>;
                      }
                      return timeStr;
                    } catch {
                      return '—';
                    }
                  })()}
                </p>
                {(item.studentCount || item.studentLevel) && (
                  <p className="text-sm text-warm-500 mt-1">
                    Up to 40 students • {formatStudentLevel(item.studentLevel)}
                  </p>
                )}
              </div>

              {/* Price & Actions */}
              <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                <div className="text-right">
                  <div className="font-semibold text-warm-900">
                    {formatPrice(item.totalPriceCents)}
                  </div>
                  {item.travelSurchargeCents > 0 && (
                    <div className="text-xs text-warm-500">
                      incl. {formatPrice(item.travelSurchargeCents)} travel
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditingItemId(editingItemId === item.id ? null : item.id)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {editingItemId === item.id ? 'Cancel' : 'Edit'}
                  </button>
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="text-danger-600 hover:text-danger-700 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>

            {/* Inline Edit Form */}
            {editingItemId === item.id && (
              <CartItemEditForm
                item={item}
                onSave={(updates) => {
                  onUpdateItem(item.id, updates);
                  setEditingItemId(null);
                }}
                onCancel={() => setEditingItemId(null)}
              />
            )}

            {/* Add-ons */}
            {(item.includeMarketingMaterials || item.groupTicketInquiry) && editingItemId !== item.id && (
              <div className="mt-4 pt-4 border-t border-warm-100 space-y-2 text-sm">
                {item.includeMarketingMaterials && (
                  <div className="flex items-center justify-between">
                    <span className="text-warm-600">Marketing Materials Package</span>
                    <span className="text-warm-900 font-medium">+$50</span>
                  </div>
                )}
                {item.groupTicketInquiry && (
                  <div className="flex items-center justify-between">
                    <span className="text-warm-600">Group Ticket Inquiry</span>
                    <span className="text-warm-500 text-xs italic">No additional cost</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Contact Selection */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-warm-900">Booking Contact</h3>
            {!showNewContactForm && (
              <button
                onClick={() => setShowNewContactForm(true)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Contact
              </button>
            )}
          </div>
          <p className="text-sm text-warm-600 mb-4">
            Select who should be the primary contact for this booking.
          </p>

          <div className="space-y-2">
            {(!contacts || contacts.length === 0) && !showNewContactForm ? (
              <div className="p-4 bg-warm-50 rounded-lg text-center">
                {!contactsLoaded ? (
                  <p className="text-warm-600 text-sm">Loading contacts...</p>
                ) : (
                  <p className="text-warm-600 text-sm">
                    No contacts found. Add a contact using the button above to continue.
                  </p>
                )}
              </div>
            ) : (
              contacts.map((contact) => (
                <label
                  key={contact.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                    ${selectedContactId === contact.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-warm-200 hover:border-warm-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="contact"
                    value={contact.id}
                    checked={selectedContactId === contact.id}
                    onChange={() => onSelectContact(contact.id)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-warm-900">
                      {contact.first_name} {contact.last_name}
                      {contact.is_primary && (
                        <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-warm-500">{contact.email}</div>
                  </div>
                </label>
              ))
            )}
          </div>

          {/* Inline New Contact Form */}
          {showNewContactForm && (
            <NewContactForm
              onSave={(contact) => {
                onContactCreated(contact);
                setShowNewContactForm(false);
              }}
              onCancel={() => setShowNewContactForm(false)}
            />
          )}
        </div>
      </div>

      {/* Order Summary Sidebar */}
      <div className="lg:col-span-1">
        <div className="card sticky top-24">
          <h3 className="font-semibold text-warm-900 mb-4">Order Summary</h3>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-warm-600">
                Subtotal ({summary.itemCount} class{summary.itemCount !== 1 ? 'es' : ''})
              </span>
              <span className="text-warm-900">{formatPrice(summary.subtotalCents)}</span>
            </div>

            {summary.travelSurchargeCents > 0 && (
              <div className="flex justify-between">
                <span className="text-warm-600">Travel Surcharge</span>
                <span className="text-warm-900">{formatPrice(summary.travelSurchargeCents)}</span>
              </div>
            )}

            {summary.marketingFeeCents > 0 && (
              <div className="flex justify-between">
                <span className="text-warm-600">Marketing Materials</span>
                <span className="text-warm-900">{formatPrice(summary.marketingFeeCents)}</span>
              </div>
            )}

            {/* Coupon Input */}
            <div className="pt-3 border-t border-warm-200">
              {appliedCoupon ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-success-50 text-success-700 text-xs font-medium">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-mono">{appliedCoupon.code}</span>
                    </span>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-warm-500 hover:text-danger-600 text-xs font-medium"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <details className="group">
                  <summary className="text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer select-none">
                    Have a coupon code?
                  </summary>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="input flex-1 text-sm font-mono"
                      disabled={isValidatingCoupon}
                    />
                    <button
                      onClick={async () => {
                        if (!couponInput.trim()) return;
                        await applyCoupon(couponInput.trim());
                        // If successful (no error set), clear input
                        setCouponInput('');
                      }}
                      disabled={isValidatingCoupon || !couponInput.trim()}
                      className="btn-primary text-sm px-3 py-2 whitespace-nowrap"
                    >
                      {isValidatingCoupon ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-danger-600 text-xs mt-1">{couponError}</p>
                  )}
                </details>
              )}
            </div>

            {/* Discount line */}
            {summary.discountCents > 0 && (
              <div className="flex justify-between text-success-600">
                <span>Discount ({summary.couponCode})</span>
                <span>-{formatPrice(summary.discountCents)}</span>
              </div>
            )}
            {appliedCoupon && summary.discountCents === 0 && (
              <div className="text-xs text-warning-600 mt-1">
                Coupon applied but doesn't affect your current cart items.
              </div>
            )}

            <div className="pt-3 border-t border-warm-200">
              <div className="flex justify-between font-semibold">
                <span className="text-warm-900">Total</span>
                <span className="text-warm-900">{formatPrice(summary.totalCents)}</span>
              </div>
            </div>

            <div className="pt-3 border-t border-warm-200">
              <div className="flex justify-between text-primary-600 font-semibold">
                <span>{summary.isFullPayment ? 'Full Payment Due Today' : 'Deposit Due Today'}</span>
                <span>{formatPrice(summary.depositCents)}</span>
              </div>
              {summary.isFullPayment ? (
                <p className="text-xs text-warm-500 mt-1">
                  Class is within 15 days — full payment required at checkout
                </p>
              ) : (
                <>
                  <p className="text-xs text-warm-500 mt-1">
                    $100 per class{summary.marketingFeeCents > 0 ? ' + marketing materials' : ''}
                  </p>
                  <p className="text-sm text-primary-600 font-semibold mt-2">
                    Balance due 15 days before class
                  </p>
                </>
              )}
            </div>
          </div>

          {!selectedContactId && (
            <div className="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800 text-center">
              Please select a booking contact above to continue
            </div>
          )}

          <button
            onClick={onProceed}
            disabled={!selectedContactId}
            className="btn-primary w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!selectedContactId ? 'Select a booking contact first' : undefined}
          >
            Continue to Terms
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Cart Item Edit Form
// =============================================================================

interface CartItemEditFormProps {
  item: CartItem;
  onSave: (updates: Partial<CartItem>) => void;
  onCancel: () => void;
}

function CartItemEditForm({ item, onSave, onCancel }: CartItemEditFormProps) {
  const [specialRequests, setSpecialRequests] = useState(item.specialRequests || '');
  const [includeMarketing, setIncludeMarketing] = useState(item.includeMarketingMaterials);
  const [groupTickets, setGroupTickets] = useState(item.groupTicketInquiry);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      specialRequests: specialRequests || null,
      includeMarketingMaterials: includeMarketing,
      groupTicketInquiry: groupTickets,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-warm-100 space-y-4">
      {/* Read-only level & student info */}
      <div className="p-3 bg-warm-50 rounded-lg text-sm text-warm-600">
        <p>Up to 40 students • {formatStudentLevel(item.studentLevel)}</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-warm-700 mb-1">
          Special Requests
        </label>
        <textarea
          value={specialRequests}
          onChange={(e) => setSpecialRequests(e.target.value)}
          className="input w-full"
          rows={2}
          maxLength={500}
          placeholder="Any special requests or notes..."
        />
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={includeMarketing}
          onChange={(e) => setIncludeMarketing(e.target.checked)}
          className="text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-warm-700">Include Marketing Materials Package (+$50)</span>
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={groupTickets}
          onChange={(e) => setGroupTickets(e.target.checked)}
          className="text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-warm-700">Group Ticket Inquiry (no additional cost)</span>
      </label>
      <div className="flex gap-3 pt-2">
        <button type="submit" className="btn-primary text-sm px-4 py-2">
          Save Changes
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm px-4 py-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// New Contact Form
// =============================================================================

interface NewContactFormProps {
  onSave: (contact: Contact) => void;
  onCancel: () => void;
}

function NewContactForm({ onSave, onCancel }: NewContactFormProps) {
  const [formData, setFormData] = useState<CreateContactData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
    receives_booking_emails: true,
    receives_marketing_emails: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      setFormError('First name, last name, email, and phone are required.');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const contact = await createContact(formData);
      onSave(contact);
    } catch (err) {
      setFormError(getFriendlyErrorMessage(err, 'Failed to create contact'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-warm-200 space-y-4">
      <h4 className="font-medium text-warm-900">New Contact</h4>
      {formError && (
        <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
          {formError}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">First Name *</label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">Last Name *</label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
            className="input w-full"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-warm-700 mb-1">Email *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          className="input w-full"
          required
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">Phone *</label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">Title</label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="input w-full"
            placeholder="e.g. Studio Owner"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={isSaving} className="btn-primary text-sm px-4 py-2">
          {isSaving ? 'Saving...' : 'Add Contact'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="btn-secondary text-sm px-4 py-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// Terms Step
// =============================================================================

interface TermsStepProps {
  terms: TermsVersion;
  summary: ReturnType<typeof useCheckout>['getCartSummary'] extends () => infer R ? R : never;
  isLoading: boolean;
  selectedContactId: string | null;
  onAccept: () => void;
  onBack: () => void;
}

function TermsStep({ terms, summary, isLoading, selectedContactId, onAccept, onBack }: TermsStepProps) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    if (isNearBottom) {
      setHasScrolled(true);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-display font-bold text-warm-900 mb-6">
        Terms & Conditions
      </h2>

      <div className="card">
        {/* Terms Content */}
        <div
          className="h-96 overflow-y-auto border border-warm-200 rounded-lg p-6 mb-6 terms-content"
          onScroll={handleScroll}
        >
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-lg font-semibold text-warm-900 border-b border-warm-200 pb-2 mb-4">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-base font-semibold text-warm-900 mt-5 mb-2">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-medium text-warm-800 mt-3 mb-1">
                  {children}
                </h3>
              ),
              p: ({ children }) => (
                <p className="text-sm text-warm-600 leading-relaxed my-2">
                  {children}
                </p>
              ),
              ul: ({ children }) => (
                <ul className="text-sm text-warm-600 my-2 pl-5 list-disc">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="text-sm text-warm-600 my-2 pl-5 list-decimal">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="text-sm text-warm-600 my-1 leading-relaxed">
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-warm-700">{children}</strong>
              ),
            }}
          >
            {terms.content}
          </ReactMarkdown>
        </div>

        {!hasScrolled && (
          <p className="text-sm text-warning-600 mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            Please scroll to read the complete terms
          </p>
        )}

        {/* Acceptance Checkbox */}
        <label className="flex items-start gap-3 mb-6">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            disabled={!hasScrolled}
            className="mt-1 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
          />
          <span className="text-sm text-warm-700">
            I have read and agree to the Terms & Conditions (Version {terms.version}, effective{' '}
            {new Date(terms.effectiveDate).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            )
          </span>
        </label>

        {/* Summary */}
        <div className="bg-warm-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-warm-600">Total Amount</p>
              <p className="text-xl font-bold text-warm-900">{formatPrice(summary.totalCents)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-warm-600">{summary.isFullPayment ? 'Full Payment Due Today' : 'Due Today'}</p>
              <p className="text-xl font-bold text-primary-600">
                {formatPrice(summary.depositCents)}
              </p>
            </div>
          </div>
          {summary.isFullPayment && (
            <p className="text-xs text-warm-500 mt-2">Class is within 15 days — full payment required at checkout</p>
          )}
        </div>

        {/* Guard: no contact selected */}
        {!selectedContactId && (
          <div className="mb-4 p-4 bg-warning-50 border border-warning-200 rounded-lg flex items-start gap-3">
            <svg
              className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-warning-800">
                No booking contact selected
              </p>
              <p className="text-sm text-warning-700 mt-0.5">
                Please go back to the cart and select a booking contact before accepting the terms.
              </p>
              <button
                onClick={onBack}
                className="mt-2 text-sm font-medium text-warning-800 underline hover:text-warning-900"
              >
                Go back to select a contact
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={onBack} className="btn-secondary flex-1">
            Back to Cart
          </button>
          <button
            onClick={onAccept}
            disabled={!accepted || isLoading || !selectedContactId}
            className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!selectedContactId ? 'Go back and select a booking contact first' : undefined}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              'Accept & Continue to Payment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Payment Step
// =============================================================================

interface PaymentStepProps {
  bookingResponse: CreateBookingResponse;
  onSuccess: (bookingId: string) => void;
  onBack: () => void;
}

function PaymentStep({ bookingResponse, onSuccess, onBack }: PaymentStepProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Payment failed');
      setIsProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/confirmation/${bookingResponse.bookingId}`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment confirmation failed');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      // Confirm the booking on the backend after successful Stripe payment
      try {
        await confirmBooking(bookingResponse.bookingId, paymentIntent.id);
        onSuccess(bookingResponse.bookingId);
      } catch (confirmErr) {
        console.error('Failed to confirm booking:', confirmErr);
        // Payment succeeded but confirmation failed - still navigate to confirmation
        // The webhook will eventually update the booking status
        onSuccess(bookingResponse.bookingId);
      }
    }
  };

  // Calculate expiry time
  const expiresAt = new Date(bookingResponse.expiresAt);
  const timeRemaining = Math.max(0, expiresAt.getTime() - Date.now());
  const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-display font-bold text-warm-900 mb-6">Payment</h2>

      <div className="card">
        {/* Timer Warning */}
        {timeRemaining > 0 && (
          <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium text-warning-800">Complete payment within 24 hours</p>
              <p className="text-sm text-warning-700">
                Time remaining: {hoursRemaining}h {minutesRemaining}m
              </p>
            </div>
          </div>
        )}

        {/* Payment Summary */}
        <div className="bg-warm-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-warm-900 mb-3">Payment Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-warm-600">Total Booking Amount</span>
              <span className="text-warm-900">{formatPrice(bookingResponse.totalAmountCents)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-warm-200 font-semibold">
              <span className="text-primary-600">
                {bookingResponse.depositAmountCents >= bookingResponse.totalAmountCents ? 'Full Payment Due Now' : 'Deposit Due Now'}
              </span>
              <span className="text-primary-600">{formatPrice(bookingResponse.depositAmountCents)}</span>
            </div>
            {bookingResponse.depositAmountCents < bookingResponse.totalAmountCents ? (
              <p className="text-xs text-warm-500">
                Remaining balance of {formatPrice(bookingResponse.totalAmountCents - bookingResponse.depositAmountCents)}{' '}
                will be automatically charged 15 days before your class.
              </p>
            ) : (
              <p className="text-xs text-warm-500">
                Class is within 15 days — full payment required at checkout.
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {/* Stripe Payment Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <PaymentElement />
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="btn-secondary flex-1"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={!stripe || isProcessing}
              className="btn-primary flex-1"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing Payment...
                </span>
              ) : (
                bookingResponse.depositAmountCents >= bookingResponse.totalAmountCents
                  ? `Pay ${formatPrice(bookingResponse.depositAmountCents)}`
                  : `Pay ${formatPrice(bookingResponse.depositAmountCents)} Deposit`
              )}
            </button>
          </div>
        </form>

        {/* Security Note */}
        <p className="mt-6 text-xs text-warm-500 text-center flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Your payment is secured by Stripe.{bookingResponse.depositAmountCents < bookingResponse.totalAmountCents && ' Your card will be saved for the balance payment.'}
        </p>
      </div>
    </div>
  );
}
